import React from 'react';
import { css, ThemeProvider as ThemeProvider$1, Global } from '@emotion/react';
import { __assign, __makeTemplateObject } from 'tslib';

// Raw tokens
/* Don't use Raw Tokens directly on custom components, use Alias Tokens instead */
var raw = {
    //  Brand
    titanWhiteLight: '#FBFBFF',
    titanWhite: '#f2f2ff',
    titanWhiteDark: '#e5e6fe',
    melrose: '#bebefd',
    portage: '#898af8',
    cornflowerBlue: '#5a5cec',
    purpleHeart: '#4b3ed5',
    governorBay: '#3a30a6',
    astronaut: '#282471',
    valhalla: '#1b1849',
    // Neutral
    white: '#FFFFFF',
    zirconWhite: '#FAFBFF',
    lilacWhite: '#F0F2FA',
    catskillWhite: '#E3E7F2',
    periwinkleGray: '#D0D5E7',
    lavenderGray: '#BCC2DB',
    rockBlue: '#A0A9CA',
    wildBlueYonder: '#808CB6',
    waikawaGray: '#64709C',
    blueBayoux: '#4D5679',
    oxfordBlue: '#3A415D',
    ebonyClay: '#2C3148',
    ebonyClayDark: '#212538',
    mirage: '#191C2C',
    black: '#000000',
    // Green
    eveningSea: '#04543F',
    shamrock: '#32C48D',
    algaeGreen: '#98E1C5',
    jaggedIce: '#C3E8D8',
    blackSqueeze: '#F2FAF7',
    // Red
    oldBrick: '#9A1C1C',
    cinnabar: '#E53E3E',
    wewak: '#F19E9E',
    cinderella: '#FDD9D9',
    linen: '#FDF2F2',
    // Yellow
    korma: '#8E4B10',
    gamboge: '#E3A008',
    buff: '#F0CF83',
    givry: '#F7E3B5',
    butteryWhite: '#FFFBEB',
    // Blue
    denim: '#1A56DB',
    malibu: '#76A9FA',
    hawkesBlue: '#D6E5FE',
    zumthor: '#EBF5FF',
    // Orange
    copperCanyon: '#8B3F18',
    piper: '#C05621',
    paleOrange: '#F8DDC4',
    bridalHeath: '#FFFAF0',
    // Rose
    frenchRose: '#F5487F',
    lavenderBlush: '#FFEFF4',
    // RoyalBlue
    royalBlue: '#6246EA',
    // Cerulean Blue
    ceruleanBlue: '#2B6CB0',
    solitude: '#EBF8FF',
    // Purple
    fuchsiaBlue: '#6B46C1',
    magnolia: '#FAF5FF',
    // Pink
    mediumRedViolet: '#B83280',
    amour: '#FFF5F7',
    // Sea Green
    greenPea: '#276749',
    feta: '#EAFDEF',
    // Persian Red
    persianRed: '#C53030',
    chablis: '#FFF5F5',
    // Brown
    paarl: '#A05D31',
    vistaWhite: '#FAF3F3',
    // Olive
    woodland: '#486C2B',
    frost: '#F2F9EC',
    // Teal
    paradiso: '#2C7A7B',
    clearDay: '#E6FFFA',
    // Tan
    hawaiianTan: '#975A16',
    apricotWhite: '#FFFFEB',
    // Indigo
    indigo: '#4C51BF',
    zircon: '#EBF4FF',
};
// Brand
var brand = {
    900: raw.valhalla,
    800: raw.astronaut,
    700: raw.governorBay,
    600: raw.purpleHeart,
    500: raw.cornflowerBlue,
    400: raw.portage,
    300: raw.melrose,
    200: raw.titanWhiteDark,
    100: raw.titanWhite,
    50: raw.titanWhiteLight,
};
// Neutral
var neutral = {
    900: raw.mirage,
    850: raw.ebonyClayDark,
    800: raw.ebonyClay,
    700: raw.oxfordBlue,
    600: raw.blueBayoux,
    500: raw.waikawaGray,
    400: raw.wildBlueYonder,
    300: raw.rockBlue,
    250: raw.lavenderGray,
    200: raw.periwinkleGray,
    150: raw.catskillWhite,
    100: raw.lilacWhite,
    50: raw.zirconWhite,
};
var green = {
    700: raw.eveningSea,
    500: raw.shamrock,
    200: raw.algaeGreen,
    100: raw.jaggedIce,
    50: raw.blackSqueeze,
};
var red = {
    700: raw.oldBrick,
    500: raw.cinnabar,
    200: raw.wewak,
    100: raw.cinderella,
    50: raw.linen,
};
var yellow = {
    700: raw.korma,
    500: raw.gamboge,
    200: raw.buff,
    100: raw.givry,
    50: raw.butteryWhite,
};
var blue = {
    700: raw.denim,
    500: raw.malibu,
    100: raw.hawkesBlue,
    50: raw.zumthor,
};
var orange = {
    700: raw.copperCanyon,
    500: raw.piper,
    100: raw.paleOrange,
    50: raw.bridalHeath,
};
var rose = {
    700: raw.frenchRose,
    100: raw.lavenderBlush,
};
var royalBlue = {
    500: raw.royalBlue,
};
var ceruleanBlue = {
    700: raw.ceruleanBlue,
    100: raw.solitude,
};
var purple = {
    700: raw.fuchsiaBlue,
    100: raw.magnolia,
};
var pink = {
    700: raw.mediumRedViolet,
    100: raw.amour,
};
var seaGreen = {
    700: raw.greenPea,
    100: raw.feta,
};
var persianRed = {
    700: raw.persianRed,
    100: raw.chablis,
};
var brown = {
    700: raw.paarl,
    100: raw.vistaWhite,
};
var olive = {
    700: raw.woodland,
    100: raw.frost,
};
var teal = {
    700: raw.paradiso,
    100: raw.clearDay,
};
var tan = {
    700: raw.hawaiianTan,
    100: raw.apricotWhite,
};
var indigo = {
    700: raw.indigo,
    100: raw.zircon,
};
var colors = {
    // Alias
    brand: brand,
    neutral: neutral,
    green: green,
    red: red,
    yellow: yellow,
    blue: blue,
    rose: rose,
    royalBlue: royalBlue,
    ceruleanBlue: ceruleanBlue,
    purple: purple,
    pink: pink,
    seaGreen: seaGreen,
    orange: orange,
    persianRed: persianRed,
    brown: brown,
    olive: olive,
    teal: teal,
    tan: tan,
    indigo: indigo,
    // Component Specific Colors
    feedback: {
        success: {
            background: raw.blackSqueeze,
            foreground: raw.eveningSea,
            border: raw.shamrock,
            icon: raw.shamrock,
        },
        error: {
            background: raw.linen,
            foreground: raw.oldBrick,
            border: raw.cinnabar,
            icon: raw.cinnabar,
        },
        warning: {
            background: raw.butteryWhite,
            foreground: raw.korma,
            border: raw.gamboge,
            icon: raw.gamboge,
        },
        info: {
            background: raw.zumthor,
            foreground: raw.denim,
            border: raw.malibu,
            icon: raw.malibu,
        },
        primary: {
            background: raw.titanWhite,
            foreground: raw.purpleHeart,
            border: raw.cornflowerBlue,
            icon: raw.cornflowerBlue,
        },
        // Extended Palette for Toast
        dark: {
            background: raw.mirage,
            foreground: raw.white,
            border: raw.mirage,
            icon: raw.white,
        },
    },
    button: {
        solid: {
            foreground: raw.white,
            primary: {
                default: raw.cornflowerBlue,
                hover: raw.governorBay,
                focus_outline: raw.melrose,
                disabled: raw.melrose,
            },
            success: {
                default: raw.shamrock,
                hover: raw.eveningSea,
                focus_outline: raw.jaggedIce,
                disabled: raw.algaeGreen,
            },
            danger: {
                default: raw.cinnabar,
                hover: raw.oldBrick,
                focus_outline: raw.cinderella,
                disabled: raw.wewak,
            },
            warning: {
                default: raw.gamboge,
                hover: raw.korma,
                focus_outline: raw.givry,
                disabled: raw.buff,
            },
        },
        outline: {
            primary: {
                default: {
                    background: raw.white,
                    foreground: raw.cornflowerBlue,
                    border: raw.periwinkleGray,
                    icon: raw.cornflowerBlue,
                },
                hover: {
                    background: raw.titanWhite,
                    icon: raw.cornflowerBlue,
                },
                focus_outline: raw.melrose,
                disabled: {
                    background: raw.titanWhiteLight,
                    foreground: raw.melrose,
                    border: raw.catskillWhite,
                },
            },
            secondary: {
                default: {
                    background: raw.white,
                    foreground: raw.blueBayoux,
                    border: raw.periwinkleGray,
                    icon: raw.wildBlueYonder,
                },
                hover: {
                    background: raw.lilacWhite,
                    icon: raw.blueBayoux,
                },
                focus_outline: raw.periwinkleGray,
                disabled: {
                    background: raw.zirconWhite,
                    foreground: raw.lavenderGray,
                    border: raw.catskillWhite,
                },
            },
        },
        ghost: {
            primary: {
                foreground: raw.cornflowerBlue,
                icon: raw.cornflowerBlue,
                hover: {
                    background: raw.titanWhite,
                    icon: raw.cornflowerBlue,
                },
                focus_outline: raw.melrose,
                disabled: raw.melrose,
            },
            secondary: {
                foreground: raw.blueBayoux,
                icon: raw.wildBlueYonder,
                hover: {
                    background: raw.lilacWhite,
                    icon: raw.blueBayoux,
                },
                focus_outline: raw.periwinkleGray,
                disabled: raw.lavenderGray,
            },
            success: {
                foreground: raw.eveningSea,
                icon: raw.shamrock,
                hover: {
                    background: raw.blackSqueeze,
                    icon: raw.eveningSea,
                },
                focus_outline: raw.jaggedIce,
                disabled: raw.algaeGreen,
            },
            danger: {
                foreground: raw.oldBrick,
                icon: raw.cinnabar,
                hover: {
                    background: raw.linen,
                    icon: raw.oldBrick,
                },
                focus_outline: raw.cinderella,
                disabled: raw.wewak,
            },
            warning: {
                foreground: raw.korma,
                icon: raw.gamboge,
                hover: {
                    background: raw.butteryWhite,
                    icon: raw.korma,
                },
                focus_outline: raw.givry,
                disabled: raw.buff,
            },
        },
        dashed: {
            secondary: {
                default: {
                    background: raw.zirconWhite,
                    foreground: raw.blueBayoux,
                    border: raw.periwinkleGray,
                    icon: raw.wildBlueYonder,
                },
                hover: {
                    background: raw.lilacWhite,
                    icon: raw.blueBayoux,
                },
                focus_outline: raw.periwinkleGray,
                disabled: {
                    background: raw.titanWhiteLight,
                    foreground: raw.lavenderGray,
                    border: raw.catskillWhite,
                },
            },
            primary: {
                default: {
                    background: raw.titanWhiteLight,
                    foreground: raw.cornflowerBlue,
                    border: raw.periwinkleGray,
                    icon: raw.cornflowerBlue,
                },
                hover: {
                    background: raw.titanWhite,
                    icon: raw.cornflowerBlue,
                },
                focus_outline: raw.melrose,
                disabled: {
                    background: raw.zirconWhite,
                    foreground: raw.melrose,
                    border: raw.catskillWhite,
                },
            },
        },
        link: {
            primary: {
                default: raw.cornflowerBlue,
                hover: raw.governorBay,
                disabled: raw.melrose,
            },
            secondary: {
                default: raw.blueBayoux,
                hover: raw.mirage,
                disabled: raw.lavenderGray,
            },
        },
    },
    avatar: {
        square: {
            background: raw.mirage,
            foreground: raw.white,
        },
        round: {
            background: raw.lilacWhite,
            foreground: raw.blueBayoux,
            inactive: raw.lavenderGray,
        },
        badge: {
            border: raw.white,
            background: raw.mirage,
        },
        group: {
            border: raw.white,
        },
    },
    switch: {
        box: {
            foreground: raw.white,
            unchecked: {
                default: raw.wildBlueYonder,
                hover: raw.waikawaGray,
                disabled: raw.catskillWhite,
                focus_outline: raw.periwinkleGray,
            },
            checked: {
                default: raw.cornflowerBlue,
                hover: raw.governorBay,
                disabled: raw.melrose,
                focus_outline: raw.melrose,
            },
        },
    },
    pill: {
        background: raw.lilacWhite,
        foreground: raw.blueBayoux,
        icon: raw.wildBlueYonder,
    },
    tooltip: {
        background: raw.ebonyClayDark,
        foreground: raw.white,
    },
    breadcrumb: {
        link: {
            default: raw.waikawaGray,
            hover: raw.mirage,
            disabled: raw.rockBlue,
            current: raw.mirage,
        },
        separator: raw.rockBlue,
        focus_outline: raw.periwinkleGray,
    },
    dialog: {
        overlay: 'RGBA(0, 0, 0, 0.5)',
        content: {
            background: raw.white,
            foreground: raw.mirage,
            shadow: 'RGBA(0, 0, 0, 0.08)',
        },
        header_border: raw.catskillWhite,
        footer: raw.zirconWhite,
    },
    // Tab
    tab: {
        pill: {
            inactive: {
                default: {
                    background: 'transparent',
                    foreground: raw.blueBayoux,
                },
                hover: raw.lilacWhite,
                focus_outline: raw.periwinkleGray,
                disabled: {
                    background: raw.zirconWhite,
                    foreground: raw.lavenderGray,
                },
            },
            active: {
                default: {
                    background: raw.titanWhite,
                    foreground: raw.cornflowerBlue,
                },
                focus_outline: raw.melrose,
                hover: raw.titanWhite,
            },
        },
        underline: {
            inactive: {
                default: {
                    background: 'transparent',
                    foreground: raw.blueBayoux,
                },
                hover: raw.lilacWhite,
                focus_outline: raw.periwinkleGray,
                disabled: {
                    background: raw.zirconWhite,
                    foreground: raw.lavenderGray,
                },
            },
            active: {
                default: {
                    background: raw.titanWhite,
                    foreground: raw.cornflowerBlue,
                },
                hover: raw.titanWhite,
                focus_outline: raw.melrose,
            },
            panel: {
                border: raw.catskillWhite,
            },
        },
    },
    dropdown: {
        // Shared with Select
        list: {
            background: raw.white,
            border: raw.periwinkleGray,
            shadow: 'rgba(0, 0, 0, 0.1)',
        },
        item: {
            background: 'transparent',
            foreground: raw.mirage,
            icon: raw.blueBayoux,
            description: raw.blueBayoux,
            hover: {
                background: raw.lilacWhite,
                icon: raw.mirage,
            },
            focus_outline: raw.periwinkleGray,
            disabled: raw.lavenderGray,
            danger: {
                foreground: raw.oldBrick,
                icon: raw.cinnabar,
                hover: {
                    background: raw.linen,
                    icon: raw.oldBrick,
                },
                focus_outline: raw.cinderella,
                disabled: raw.wewak,
            },
            warning: {
                foreground: raw.korma,
                icon: raw.gamboge,
                hover: {
                    background: raw.butteryWhite,
                    icon: raw.korma,
                },
                focus_outline: raw.givry,
                disabled: raw.buff,
            },
            selected: {
                background: raw.white,
                foreground: raw.purpleHeart,
                description: raw.cornflowerBlue,
                hover: raw.lilacWhite,
                icon: raw.cornflowerBlue,
                focus_outline: raw.periwinkleGray,
            },
        },
    },
    // Select (not shared with Dropdown Menu)
    select: {
        trigger: {
            background: raw.white,
            foreground: raw.mirage,
            hover: raw.zirconWhite,
        },
    },
    // Input and Textarea
    input: {
        placeholder: raw.wildBlueYonder,
        value: raw.mirage,
        icon: raw.wildBlueYonder,
        background: raw.white,
        initial: {
            border: raw.periwinkleGray,
            readonly: {
                background: raw.zirconWhite,
                value: raw.blueBayoux,
            },
        },
        focus: {
            border: raw.cornflowerBlue,
            readonly: {
                background: raw.zirconWhite,
                border: raw.lavenderGray,
            },
        },
        disabled: {
            background: raw.lilacWhite,
            value: raw.wildBlueYonder,
        },
        invalid: {
            border: raw.cinnabar,
        },
        valid: {
            border: raw.shamrock,
        },
        // Label for all form controls
        label: {
            default: raw.mirage,
            disabled: raw.rockBlue,
        },
        hint: {
            default: raw.waikawaGray,
            disabled: raw.rockBlue,
        },
        addon: {
            background: raw.white,
            foreground: raw.blueBayoux,
            border: raw.periwinkleGray,
        },
    },
    // Nav
    nav: {
        background: raw.zirconWhite,
        header: {
            foreground: raw.mirage,
            focus_outline: raw.periwinkleGray,
        },
        item: {
            inactive: {
                default: {
                    background: 'transparent',
                    foreground: raw.mirage,
                },
                hover: {
                    background: raw.lilacWhite,
                },
                focus_outline: raw.periwinkleGray,
            },
            active: {
                default: {
                    background: raw.titanWhiteDark,
                    foreground: raw.purpleHeart,
                },
                hover: {
                    background: raw.titanWhiteDark,
                },
                focus_outline: raw.melrose,
            },
        },
    },
    // Radio & Checkbox
    checkbox: {
        box: {
            unchecked: {
                default: {
                    background: raw.white,
                    border: raw.wildBlueYonder,
                },
                hover: raw.waikawaGray,
                disabled: {
                    background: raw.zirconWhite,
                    border: raw.catskillWhite,
                },
                focus_outline: raw.periwinkleGray,
            },
            checked: {
                default: raw.cornflowerBlue,
                icon: raw.white,
                hover: raw.governorBay,
                disabled: raw.melrose,
                focus_outline: raw.melrose,
            },
        },
    },
    /* Color variables that will be replaced by Component or Alias tokens
      Please use the alias tokens in your app components, if there are not component specific tokens created.
    */
    /* The color tokens below will be deprecated */
    primary: raw.cornflowerBlue,
    white: '#FFFFFF',
    text: raw.mirage,
    textLight: raw.waikawaGray,
    textCaption: raw.wildBlueYonder,
    textDark: raw.mirage,
    heading: raw.mirage,
    /* These colors are only used in Baukasten and it will be replaced with component specific tokens,
    don't use on non Baukasten components, use aliases instead  */
    layoutBorder: raw.lilacWhite,
    backgroundBorder: raw.waikawaGray,
    default: raw.waikawaGray,
    link: raw.cornflowerBlue,
    disabled: '#8f8f8f4f',
    label: raw.waikawaGray,
    labelHover: raw.oxfordBlue,
    labelOutlineHover: raw.periwinkleGray,
    // Form
    inputErrorText: raw.oldBrick,
    selectBg: raw.white,
    selectBgHover: raw.catskillWhite,
    selectListText: raw.mirage,
    multiSelectItemBg: raw.catskillWhite,
    // Card
    cardBg: raw.white,
    tintedCardHeaderBg: raw.catskillWhite,
    cardBorder: raw.lilacWhite,
    // Divider
    dividerColor: raw.periwinkleGray,
    // Menu
    menuBg: 'transparent',
    menuHeaderText: raw.waikawaGray,
    menuItemInactiveText: raw.mirage,
    menuItemInactiveHoverBg: raw.lilacWhite,
    menuItemInactiveFocus: raw.periwinkleGray,
    menuItemActiveText: raw.cornflowerBlue,
    menuItemActiveBg: raw.titanWhite,
    menuItemActiveFocus: raw.melrose,
};

var fonts = {
    body: 'Inter, -apple-system,BlinkMacSystemFont,Segoe UI,Helvetica,Arial,sans-serif,Apple Color Emoji,Segoe UI Emoji;',
    heading: 'Inter, -apple-system,BlinkMacSystemFont,Segoe UI,Helvetica,Arial,sans-serif,Apple Color Emoji,Segoe UI Emoji;',
    monospace: 'Source Code Pro, SFMono-Regular,Consolas,Liberation Mono,Menlo,monospace',
};
/* Text styles and font sizes is under construction */
var fontSizes = {
    pill: '12px',
    tiny: '12px',
    sectionTitle: '12px',
    interface: '13px',
    copy: '15px',
    icon: '1.5em',
    h6: '14px',
    h5: '16px',
    h4: '18px',
    h3: '20px',
    h2: '24px',
    h1: '28px',
    inviteMembersInfo: '18px',
    stepTitle: '17px',
};
var fontWeights = {
    '100': 100,
    '200': 200,
    '300': 300,
    '400': 400,
    '500': 500,
    '600': 600,
    '700': 700,
    normal: 400,
    body: 400,
    heading: 400,
    medium: 500,
    semiBold: 600,
    bold: 700,
    activeMenuItem: 700,
    h1: 700,
    h2: 600,
    h3: 500,
    h4: 400,
    h5: 300,
    h6: 200,
};
var lineHeights = {
    none: '1em',
    small: '16px',
    sm: '18px',
    medium: '20px',
    regular: '24px',
    copy: '23px',
    double: '48px',
    heading: '24px',
    inviteMembersInfo: '28px',
    info: '20px',
    h1: '48px',
    h2: '40px',
    h3: '32px',
    h4: '24px',
    h5: '20px',
    h6: '20px',
    copySmall: '18px',
    error: '22px',
    dialogHeader: '22px',
};
var letterSpacings = {
    sectionTitle: '1.2px',
    cardStatusInfo: '1.2px',
};

var space = {
    /*
      Please use these space tokens
    */
    '0': '0',
    '4': '4px',
    '6': '6px',
    '8': '8px',
    '10': '10px',
    '12': '12px',
    '20': '20px',
    '24': '24px',
    '16': '16px',
    '32': '32px',
    '36': '36px',
    '40': '40px',
    '48': '48px',
    '52': '52px',
    '56': '56px',
    '64': '64px',
    '96': '96px',
    /* These space tokens below will be deprecated */
    none: 0,
    xxs: 2,
    xs: 4,
    s: 8,
    sm: 12,
    m: 16,
    ml: 20,
    l: 24,
    xl: 32,
    layoutXS: 16,
    layoutS: 24,
    layoutSM: 28,
    layoutM: 32,
    layoutML: 40,
    layoutL: 48,
    layoutXL: 64,
    layoutXXL: 96,
};
var negativeSpace = Object.keys(space).reduce(function (neg, key) {
    if (key === 'none')
        return neg;
    var upperKey = key.charAt(0).toUpperCase() + key.slice(1);
    neg["neg".concat(upperKey)] = -space[key];
    return neg;
}, {});
var space$1 = __assign(__assign({}, space), negativeSpace);

/* Please keep all keys "string" or TS autocomplete wont work. */
var avatarSizes = {
    '16': '16px',
    '24': '24px',
    '32': '32px',
    '36': '36px',
    '40': '40px',
    '80': '80px',
    '148': '148px',
    '180': '180px',
};
var sizes = {
    auto: 'auto',
    // Baukasten component specific sizes
    avatar: avatarSizes,
    spinner: {
        xs: '0.75rem',
        s: '1rem',
        m: '1.5rem',
        l: '2rem',
        xl: '3rem',
    },
    button: {
        small: '24px',
        medium: '32px',
        large: '40px',
    },
    dialog: {
        medium: '588px',
        large: '792px',
        fullWidth: '100%',
    },
    // For components that use the iconSize or the  size prop
    icon: {
        '12': '12px',
        '16': '16px',
        '20': '20px',
        '24': '24px',
        '32': '32px',
        '48': '48px',
    },
    progress: {
        '5': '5px',
        '8': '8px',
        '16': '16px',
    },
    /* For components that use the size prop or width height,
      minWidth, maxWidth, minHeight, maxHeight */
    /* size tokens can only be multiples of 4 */
    '4': '4px',
    '8': '8px',
    '12': '12px',
    '16': '16px',
    '20': '20px',
    '24': '24px',
    '28': '28px',
    '32': '32px',
    '36': '36px',
    '40': '40px',
    '44': '44px',
    '48': '48px',
    '52': '52px',
    '56': '56px',
    '60': '60px',
    '64': '64px',
    '72': '72px',
    '80': '80px',
    '96': '96px',
    '110': '110px',
    '116': '116px',
    '120': '120px',
    '140': '140px',
    '156': '156px',
    '180': '180px',
    '184': '184px',
    '188': '188px',
    '200': '200px',
    '208': '208px',
    '212': '212px',
    '220': '220px',
    '236': '236px',
    '240': '240px',
    '248': '248px',
    '252': '252px',
    '256': '256px',
    '264': '264px',
    '272': '272px',
    '280': '280px',
    '288': '288px',
    '300': '300px',
    '320': '320px',
    '328': '328px',
    '340': '340px',
    '348': '348px',
    '360': '360px',
    '380': '380px',
    '364': '364px',
    '368': '368px',
    '384': '384px',
    '400': '400px',
    '420': '420px',
    '440': '440px',
    '456': '456px',
    '468': '468px',
    '500': '500px',
    '520': '520px',
    '564': '564px',
    '588': '588px',
    '600': '600px',
    '672': '672px',
    '688': '688px',
    '720': '720px',
    '744': '744px',
    '748': '748px',
    '792': '792px',
    '900': '900px',
    '912': '912px',
    '960': '960px',
    '996': '996px',
    '1440': '1440px',
};

var shadows = {
    light: '0 2px 4px rgba(0,0,0,0.05)',
    inputFocus: 'none',
    inputErrorFocus: 'none',
    inputAutofill: '0 0 0px 50px #fff inset',
    rightSideBar: '-1px 0 4px rgba(0,0,0,0.05)',
    input: '0 2px 4px rgba(0,0,0,0.05)',
    topBar: 'none',
    menuItemInactiveFocus: "0 0 0 2px ".concat(colors.menuItemInactiveFocus),
    menuItemActiveFocus: "0 0 0 2px ".concat(colors.menuItemActiveFocus),
    // Baukasten Component specific shadows, don't use them in custom components
    dropdownMenuList: "0px 8px 14px 2px ".concat(colors.dropdown.list.shadow),
    toast: '0 8px 14px 2px rgba(0, 0, 0, 0.1)',
    card: '0px 2px 6px rgba(0,0,0,0.08)',
    cardHover: '0px 2px 6px rgba(0,0,0,0.1)',
    tooltip: '0 2px 4px rgba(0, 0, 0, 0.2)',
};

var radii = {
    '2': '2px',
    '4': '4px',
    '8': '8px',
    '16': '16px',
    round: '9999px',
    /* These radii tokens below will be deprecated */
    small: '4px',
    medium: '8px',
    large: '16px',
    card: '4px',
    input: '2px',
    tooltip: '3px',
};

// @ts-expect-error
var breakpoints = [
    '576px',
    '768px',
    '992px',
    '1200px',
];
breakpoints.sm = breakpoints[0];
breakpoints.md = breakpoints[1];
breakpoints.lg = breakpoints[2];
breakpoints.xl = breakpoints[3];

var text = {
    heading: {
        fontFamily: 'heading',
        lineHeight: 'heading',
        fontWeight: 'heading',
    },
};

var zIndexes = {
    modal: 2000,
    tooltip: 2010,
    tooltipArrow: '-1',
};

var borders = {
    layout: '1px solid',
    input: '1px solid',
    card: '1px solid',
    toast: '1px solid',
    disabledLayout: '1px dotted',
    tableHeader: '2px solid',
    activeMenu: '4px solid',
};
var borderWidths = {
    activeMenu: '4px',
};

var gap = {
    none: 0,
    '4': 4,
    '8': 8,
    '12': 12,
    '16': 16,
    '20': 20,
    '24': 24,
    '28': 28,
    '32': 32,
    '36': 36,
    '40': 40,
    '48': 48,
    '52': 52,
    '56': 56,
    '64': 64,
    '96': 96,
};

// tokens
var baukastenTheme = {
    borders: borders,
    borderWidths: borderWidths,
    colors: colors,
    fonts: fonts,
    fontSizes: fontSizes,
    fontWeights: fontWeights,
    lineHeights: lineHeights,
    letterSpacings: letterSpacings,
    space: space$1,
    sizes: sizes,
    shadows: shadows,
    radii: radii,
    text: text,
    breakpoints: breakpoints,
    zIndexes: zIndexes,
    gap: gap,
};

var makeGlobalStyles = (function (theme) {
    var fonts = theme.fonts, lineHeights = theme.lineHeights, fontSizes = theme.fontSizes, colors = theme.colors, fontWeights = theme.fontWeights;
    return css(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n    html {\n      font: ", " / ", " ", ";\n      box-sizing: border-box;\n      -ms-text-size-adjust: 100%;\n      -webkit-text-size-adjust: 100%;\n      width: 100%;\n    }\n    body {\n      margin: 0;\n      color: ", ";\n      font-weight: ", ";\n      word-wrap: break-word;\n      -webkit-font-smoothing: antialiased;\n      -moz-osx-font-smoothing: grayscale;\n    }\n    h1,\n    h2,\n    h3,\n    h4,\n    h5,\n    h6 {\n      margin: 0;\n      padding: 0;\n      color: ", ";\n    }\n    h1 {\n      line-height: ", ";\n      font-size: ", ";\n      font-weight: ", ";\n    }\n    h2 {\n      line-height: ", ";\n      font-size: ", ";\n      font-weight: ", ";\n    }\n    h3 {\n      line-height: ", ";\n      font-size: ", ";\n      font-weight: ", ";\n    }\n    h4 {\n      line-height: ", ";\n      font-size: ", ";\n      font-weight: ", ";\n    }\n    h5 {\n      line-height: ", ";\n      font-size: ", ";\n      font-weight: ", ";\n    }\n    h6 {\n      line-height: ", ";\n      font-size: ", ";\n      font-weight: ", ";\n    }\n\n    a {\n      color: inherit;\n      text-decoration: none;\n      background-color: transparent;\n      -webkit-text-decoration-skip: objects;\n    }\n    a:active,\n    a:hover {\n      outline-width: 0;\n    }\n\n    * {\n      box-sizing: inherit;\n    }\n    *:before {\n      box-sizing: inherit;\n    }\n    *:after {\n      box-sizing: inherit;\n    }\n\n    article,\n    aside,\n    details,\n    figcaption,\n    figure,\n    footer,\n    header,\n    main,\n    menu,\n    nav,\n    section,\n    summary {\n      display: block;\n    }\n    audio,\n    canvas,\n    progress,\n    video {\n      display: inline-block;\n    }\n    audio:not([controls]) {\n      display: none;\n      height: 0;\n    }\n    progress {\n      vertical-align: baseline;\n    }\n    [hidden],\n    template {\n      display: none;\n    }\n    abbr[title] {\n      border-bottom: none;\n      text-decoration: underline;\n      text-decoration: underline dotted;\n    }\n    b,\n    strong {\n      font-weight: inherit;\n      font-weight: bolder;\n    }\n    dfn {\n      font-style: italic;\n    }\n    mark {\n      background-color: #ff0;\n      color: #000;\n    }\n    small {\n      font-size: 80%;\n    }\n    sub,\n    sup {\n      font-size: 75%;\n      line-height: 0;\n      position: relative;\n      vertical-align: baseline;\n    }\n    sub {\n      bottom: -0.25em;\n    }\n    sup {\n      top: -0.5em;\n    }\n    img {\n      border-style: none;\n    }\n    svg:not(:root) {\n      overflow: hidden;\n    }\n    code,\n    kbd,\n    pre,\n    samp {\n      font-family: monospace, monospace;\n      font-size: 1em;\n    }\n    figure {\n      margin: 1em 40px;\n    }\n    hr {\n      box-sizing: content-box;\n      height: 0;\n      overflow: visible;\n    }\n    button,\n    input,\n    optgroup,\n    select,\n    textarea {\n      font: inherit;\n      margin: 0;\n    }\n    optgroup {\n      font-weight: 700;\n    }\n    button,\n    input {\n      overflow: visible;\n    }\n    input:required {\n      box-shadow: none;\n    }\n    button,\n    select {\n      text-transform: none;\n    }\n    [type='reset'],\n    [type='submit'],\n    button,\n    html [type='button'] {\n      -webkit-appearance: button;\n    }\n    [type='button']::-moz-focus-inner,\n    [type='reset']::-moz-focus-inner,\n    [type='submit']::-moz-focus-inner,\n    button::-moz-focus-inner {\n      border-style: none;\n      padding: 0;\n    }\n    [type='button']:-moz-focusring,\n    [type='reset']:-moz-focusring,\n    [type='submit']:-moz-focusring,\n    button:-moz-focusring {\n      outline: 1px dotted ButtonText;\n    }\n    fieldset {\n      border: none;\n      margin: 0;\n      padding: 0;\n    }\n    legend {\n      box-sizing: border-box;\n      color: inherit;\n      display: table;\n      max-width: 100%;\n      padding: 0;\n      white-space: normal;\n    }\n    textarea {\n      overflow: auto;\n    }\n    [type='checkbox'],\n    [type='radio'] {\n      box-sizing: border-box;\n      padding: 0;\n    }\n    [type='number']::-webkit-inner-spin-button,\n    [type='number']::-webkit-outer-spin-button {\n      height: auto;\n    }\n    [type='search'] {\n      -webkit-appearance: textfield;\n      outline-offset: -2px;\n    }\n    [type='search']::-webkit-search-cancel-button,\n    [type='search']::-webkit-search-decoration {\n      -webkit-appearance: none;\n    }\n    ::-webkit-input-placeholder {\n      color: inherit;\n      opacity: 0.34;\n    }\n    ::-webkit-file-upload-button {\n      -webkit-appearance: button;\n      font: inherit;\n    }\n    img {\n      max-width: 100%;\n      margin: 0;\n      padding: 0;\n    }\n    img,\n    hgroup,\n    ul,\n    ol,\n    dl,\n    dd,\n    p,\n    figure,\n    pre,\n    table,\n    fieldset,\n    blockquote,\n    form,\n    noscript,\n    iframe,\n    hr,\n    address {\n      margin: 0;\n      padding: 0;\n    }\n    iframe {\n      border: 0;\n    }\n    ul {\n      list-style-position: outside;\n      list-style-image: none;\n    }\n    ol {\n      list-style-position: outside;\n      list-style-image: none;\n    }\n    pre {\n      font-size: 0.85rem;\n      line-height: 1.5rem;\n    }\n    table {\n      font-size: 1rem;\n      line-height: 1.5rem;\n      border-collapse: collapse;\n      width: 100%;\n    }\n    blockquote {\n      font-size: 1.22474rem;\n      line-height: 1.5rem;\n      border-left: 0.25rem solid #eceeef;\n    }\n    hr {\n      background: hsla(0, 0%, 0%, 0.2);\n      border: none;\n      height: 1px;\n    }\n    b,\n    strong,\n    dt,\n    th {\n      font-weight: bold;\n    }\n    ol li {\n      padding-left: 0;\n    }\n    ul li {\n      padding-left: 0;\n    }\n    blockquote *:last-child {\n      margin-bottom: 0;\n    }\n    li *:last-child {\n      margin-bottom: 0;\n    }\n    p *:last-child {\n      margin-bottom: 0;\n    }\n    code {\n      font-size: 0.85rem;\n      line-height: 1.5rem;\n    }\n    kbd {\n      font-size: 0.85rem;\n      line-height: 1.5rem;\n    }\n    samp {\n      font-size: 0.85rem;\n      line-height: 1.5rem;\n    }\n    abbr {\n      border-bottom: 1px dotted hsla(0, 0%, 0%, 0.5);\n      cursor: help;\n    }\n    acronym {\n      border-bottom: 1px dotted hsla(0, 0%, 0%, 0.5);\n      cursor: help;\n    }\n    abbr[title] {\n      border-bottom: 1px dotted hsla(0, 0%, 0%, 0.5);\n      cursor: help;\n      text-decoration: none;\n    }\n    thead {\n      text-align: left;\n    }\n    th {\n      text-align: left;\n      border-bottom: 1px solid hsla(0, 0%, 0%, 0.12);\n      font-feature-settings: 'tnum';\n      -moz-font-feature-settings: 'tnum';\n      -ms-font-feature-settings: 'tnum';\n      -webkit-font-feature-settings: 'tnum';\n      padding-left: 1rem;\n      padding-right: 1rem;\n      padding-top: 0.75rem;\n      padding-bottom: calc(0.75rem - 1px);\n    }\n    blockquote > :last-child {\n      margin-bottom: 0;\n    }\n    blockquote cite {\n      font-size: 1rem;\n      line-height: 1.5rem;\n      color: hsla(204, 5.275997423999999%, 0%, 0.46);\n      font-weight: 400;\n      font-style: normal;\n    }\n    &::-webkit-scrollbar {\n      width: 8px;\n      height: 8px;\n      background-color: ", ";\n    }\n    &::-webkit-scrollbar-thumb {\n      background-color: ", ";\n      border-radius: 0;\n    }\n    /* Hide filestack logo in asset picker */\n    .fsp-picker__brand {\n      display: none !important;\n    }\n    :root {\n      --reach-dialog: 1;\n      --reach-tabs: 1;\n      --reach-tooltip: 1;\n      --reach-menu-button: 1;\n    }\n  "], ["\n    html {\n      font: ", " / ", " ", ";\n      box-sizing: border-box;\n      -ms-text-size-adjust: 100%;\n      -webkit-text-size-adjust: 100%;\n      width: 100%;\n    }\n    body {\n      margin: 0;\n      color: ", ";\n      font-weight: ", ";\n      word-wrap: break-word;\n      -webkit-font-smoothing: antialiased;\n      -moz-osx-font-smoothing: grayscale;\n    }\n    h1,\n    h2,\n    h3,\n    h4,\n    h5,\n    h6 {\n      margin: 0;\n      padding: 0;\n      color: ", ";\n    }\n    h1 {\n      line-height: ", ";\n      font-size: ", ";\n      font-weight: ", ";\n    }\n    h2 {\n      line-height: ", ";\n      font-size: ", ";\n      font-weight: ", ";\n    }\n    h3 {\n      line-height: ", ";\n      font-size: ", ";\n      font-weight: ", ";\n    }\n    h4 {\n      line-height: ", ";\n      font-size: ", ";\n      font-weight: ", ";\n    }\n    h5 {\n      line-height: ", ";\n      font-size: ", ";\n      font-weight: ", ";\n    }\n    h6 {\n      line-height: ", ";\n      font-size: ", ";\n      font-weight: ", ";\n    }\n\n    a {\n      color: inherit;\n      text-decoration: none;\n      background-color: transparent;\n      -webkit-text-decoration-skip: objects;\n    }\n    a:active,\n    a:hover {\n      outline-width: 0;\n    }\n\n    * {\n      box-sizing: inherit;\n    }\n    *:before {\n      box-sizing: inherit;\n    }\n    *:after {\n      box-sizing: inherit;\n    }\n\n    article,\n    aside,\n    details,\n    figcaption,\n    figure,\n    footer,\n    header,\n    main,\n    menu,\n    nav,\n    section,\n    summary {\n      display: block;\n    }\n    audio,\n    canvas,\n    progress,\n    video {\n      display: inline-block;\n    }\n    audio:not([controls]) {\n      display: none;\n      height: 0;\n    }\n    progress {\n      vertical-align: baseline;\n    }\n    [hidden],\n    template {\n      display: none;\n    }\n    abbr[title] {\n      border-bottom: none;\n      text-decoration: underline;\n      text-decoration: underline dotted;\n    }\n    b,\n    strong {\n      font-weight: inherit;\n      font-weight: bolder;\n    }\n    dfn {\n      font-style: italic;\n    }\n    mark {\n      background-color: #ff0;\n      color: #000;\n    }\n    small {\n      font-size: 80%;\n    }\n    sub,\n    sup {\n      font-size: 75%;\n      line-height: 0;\n      position: relative;\n      vertical-align: baseline;\n    }\n    sub {\n      bottom: -0.25em;\n    }\n    sup {\n      top: -0.5em;\n    }\n    img {\n      border-style: none;\n    }\n    svg:not(:root) {\n      overflow: hidden;\n    }\n    code,\n    kbd,\n    pre,\n    samp {\n      font-family: monospace, monospace;\n      font-size: 1em;\n    }\n    figure {\n      margin: 1em 40px;\n    }\n    hr {\n      box-sizing: content-box;\n      height: 0;\n      overflow: visible;\n    }\n    button,\n    input,\n    optgroup,\n    select,\n    textarea {\n      font: inherit;\n      margin: 0;\n    }\n    optgroup {\n      font-weight: 700;\n    }\n    button,\n    input {\n      overflow: visible;\n    }\n    input:required {\n      box-shadow: none;\n    }\n    button,\n    select {\n      text-transform: none;\n    }\n    [type='reset'],\n    [type='submit'],\n    button,\n    html [type='button'] {\n      -webkit-appearance: button;\n    }\n    [type='button']::-moz-focus-inner,\n    [type='reset']::-moz-focus-inner,\n    [type='submit']::-moz-focus-inner,\n    button::-moz-focus-inner {\n      border-style: none;\n      padding: 0;\n    }\n    [type='button']:-moz-focusring,\n    [type='reset']:-moz-focusring,\n    [type='submit']:-moz-focusring,\n    button:-moz-focusring {\n      outline: 1px dotted ButtonText;\n    }\n    fieldset {\n      border: none;\n      margin: 0;\n      padding: 0;\n    }\n    legend {\n      box-sizing: border-box;\n      color: inherit;\n      display: table;\n      max-width: 100%;\n      padding: 0;\n      white-space: normal;\n    }\n    textarea {\n      overflow: auto;\n    }\n    [type='checkbox'],\n    [type='radio'] {\n      box-sizing: border-box;\n      padding: 0;\n    }\n    [type='number']::-webkit-inner-spin-button,\n    [type='number']::-webkit-outer-spin-button {\n      height: auto;\n    }\n    [type='search'] {\n      -webkit-appearance: textfield;\n      outline-offset: -2px;\n    }\n    [type='search']::-webkit-search-cancel-button,\n    [type='search']::-webkit-search-decoration {\n      -webkit-appearance: none;\n    }\n    ::-webkit-input-placeholder {\n      color: inherit;\n      opacity: 0.34;\n    }\n    ::-webkit-file-upload-button {\n      -webkit-appearance: button;\n      font: inherit;\n    }\n    img {\n      max-width: 100%;\n      margin: 0;\n      padding: 0;\n    }\n    img,\n    hgroup,\n    ul,\n    ol,\n    dl,\n    dd,\n    p,\n    figure,\n    pre,\n    table,\n    fieldset,\n    blockquote,\n    form,\n    noscript,\n    iframe,\n    hr,\n    address {\n      margin: 0;\n      padding: 0;\n    }\n    iframe {\n      border: 0;\n    }\n    ul {\n      list-style-position: outside;\n      list-style-image: none;\n    }\n    ol {\n      list-style-position: outside;\n      list-style-image: none;\n    }\n    pre {\n      font-size: 0.85rem;\n      line-height: 1.5rem;\n    }\n    table {\n      font-size: 1rem;\n      line-height: 1.5rem;\n      border-collapse: collapse;\n      width: 100%;\n    }\n    blockquote {\n      font-size: 1.22474rem;\n      line-height: 1.5rem;\n      border-left: 0.25rem solid #eceeef;\n    }\n    hr {\n      background: hsla(0, 0%, 0%, 0.2);\n      border: none;\n      height: 1px;\n    }\n    b,\n    strong,\n    dt,\n    th {\n      font-weight: bold;\n    }\n    ol li {\n      padding-left: 0;\n    }\n    ul li {\n      padding-left: 0;\n    }\n    blockquote *:last-child {\n      margin-bottom: 0;\n    }\n    li *:last-child {\n      margin-bottom: 0;\n    }\n    p *:last-child {\n      margin-bottom: 0;\n    }\n    code {\n      font-size: 0.85rem;\n      line-height: 1.5rem;\n    }\n    kbd {\n      font-size: 0.85rem;\n      line-height: 1.5rem;\n    }\n    samp {\n      font-size: 0.85rem;\n      line-height: 1.5rem;\n    }\n    abbr {\n      border-bottom: 1px dotted hsla(0, 0%, 0%, 0.5);\n      cursor: help;\n    }\n    acronym {\n      border-bottom: 1px dotted hsla(0, 0%, 0%, 0.5);\n      cursor: help;\n    }\n    abbr[title] {\n      border-bottom: 1px dotted hsla(0, 0%, 0%, 0.5);\n      cursor: help;\n      text-decoration: none;\n    }\n    thead {\n      text-align: left;\n    }\n    th {\n      text-align: left;\n      border-bottom: 1px solid hsla(0, 0%, 0%, 0.12);\n      font-feature-settings: 'tnum';\n      -moz-font-feature-settings: 'tnum';\n      -ms-font-feature-settings: 'tnum';\n      -webkit-font-feature-settings: 'tnum';\n      padding-left: 1rem;\n      padding-right: 1rem;\n      padding-top: 0.75rem;\n      padding-bottom: calc(0.75rem - 1px);\n    }\n    blockquote > :last-child {\n      margin-bottom: 0;\n    }\n    blockquote cite {\n      font-size: 1rem;\n      line-height: 1.5rem;\n      color: hsla(204, 5.275997423999999%, 0%, 0.46);\n      font-weight: 400;\n      font-style: normal;\n    }\n    &::-webkit-scrollbar {\n      width: 8px;\n      height: 8px;\n      background-color: ", ";\n    }\n    &::-webkit-scrollbar-thumb {\n      background-color: ", ";\n      border-radius: 0;\n    }\n    /* Hide filestack logo in asset picker */\n    .fsp-picker__brand {\n      display: none !important;\n    }\n    :root {\n      --reach-dialog: 1;\n      --reach-tabs: 1;\n      --reach-tooltip: 1;\n      --reach-menu-button: 1;\n    }\n  "])), fontSizes.interface, lineHeights.regular, fonts.body, colors.text, fontWeights.body, colors.heading, lineHeights.h1, fontSizes.h1, fontWeights.h1, lineHeights.h2, fontSizes.h2, fontWeights.h2, lineHeights.h3, fontSizes.h3, fontWeights.h3, lineHeights.h4, fontSizes.h4, fontWeights.h4, lineHeights.h5, fontSizes.h5, fontWeights.h5, lineHeights.h6, fontSizes.h6, fontWeights.h6, colors.neutral[25], colors.neutral[100]);
});
var templateObject_1;

var BaukastenThemeProvider = function (props) {
    var _a = props.theme, theme = _a === void 0 ? baukastenTheme : _a, _b = props.children, children = _b === void 0 ? null : _b, _c = props.global, global = _c === void 0 ? false : _c;
    var globalStyle = makeGlobalStyles(baukastenTheme);
    return (React.createElement(ThemeProvider$1, { theme: theme },
        global && React.createElement(Global, { styles: globalStyle }),
        children));
};
var ThemeProvider = function (props) {
    var _a = props.theme, theme = _a === void 0 ? baukastenTheme : _a, themeOverride = props.themeOverride, _b = props.global, global = _b === void 0 ? false : _b, _c = props.children, children = _c === void 0 ? null : _c;
    // TODO reflect theme overrides in muiTheme
    return (React.createElement(ThemeProvider$1, { theme: theme },
        React.createElement(ThemeProvider$1, { theme: themeOverride || {} },
            global && React.createElement(Global, { styles: makeGlobalStyles(baukastenTheme) }),
            children)));
};

export { BaukastenThemeProvider, ThemeProvider, baukastenTheme };
//# sourceMappingURL=index.esm.js.map
